import { z } from 'zod';
import { ClientEvent, StoredEvent } from './events.ts';
import { Cart, Site } from './catalog.ts';

/**
 * One endpoint, bidirectional, one round trip: push the outbox and pull everything new in
 * the same call. That matters on a connection that drops — two sequential calls means twice
 * the chance of finishing half the job.
 */

export const SyncRequest = z.object({
  device_id: z.string().uuid(),
  /** Last server seq this device has seen. 0 on a fresh install (full catalogue follows). */
  cursor: z.number().int().min(0),
  /**
   * The id of the event the client believes sits at `cursor` — a fence.
   *
   * `seq` alone is not a safe cursor across the life of a database. A truncate-and-reseed,
   * or a restore from backup, rewinds the sequence and hands the same seq numbers to
   * different events. A client holding the old cursor then pulls the new events as if they
   * were additions and silently keeps its stale ones for ever, because events are keyed by
   * uuid and nothing collides. The result is a local log that mixes two different histories
   * and never recovers.
   *
   * Sending the id lets the server notice the mismatch and order a resync.
   */
  cursor_event_id: z.string().uuid().nullable().optional(),
  /** Pending outbox, oldest first. Capped to keep a single request bounded. */
  events: z.array(ClientEvent).max(500),
});
export type SyncRequest = z.infer<typeof SyncRequest>;

export const SyncRejection = z.object({
  id: z.string().uuid(),
  reason: z.enum([
    'unknown_cart',
    'unknown_site',
    'invalid_payload',
  ]),
  detail: z.string().optional(),
});
export type SyncRejection = z.infer<typeof SyncRejection>;

export const SyncResponse = z.object({
  /** Event ids durably stored. The client clears exactly these from the outbox. */
  accepted: z.array(z.string().uuid()),
  /**
   * Permanently unacceptable events. The client must drop these rather than retry forever,
   * and surface them — a scan rejected as unknown_cart is a real operational fact.
   */
  rejected: z.array(SyncRejection),
  /** Events from other devices since `cursor`, so every device's projection converges. */
  events: z.array(StoredEvent),
  /**
   * The whole catalogue, every sync. At ~200 carts and a handful of sites this is a couple
   * of dozen kilobytes, and sending it whole removes an entire class of delta-tracking bug.
   * Revisit if the fleet ever reaches a few thousand.
   */
  catalog: z.object({ sites: z.array(Site), carts: z.array(Cart) }),
  /** New cursor to persist. */
  cursor: z.number().int().min(0),
  /** True when more remains beyond this page — the client should sync again immediately. */
  has_more: z.boolean(),
  /**
   * The server does not recognise the client's cursor, so the local replica belongs to a
   * history that no longer exists. The client must discard its cached events and catalogue
   * — never its unsent outbox — and pull again from zero.
   */
  reset: z.boolean(),
  server_time: z.string().datetime(),
  config: z.object({ stale_after_days: z.number().int().positive() }),
});
export type SyncResponse = z.infer<typeof SyncResponse>;
